"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Download, Loader2 } from "lucide-react";
import type { BaseEntity } from "./types";
import type {
  ExportOptions,
  ExportStats,
  SimpleTableMobileExportProps,
} from "./SimpleTableMobileExport/types";
import { ExportSummary } from "./SimpleTableMobileExport/ExportSummary";
import { ExportFormatPicker } from "./SimpleTableMobileExport/ExportFormatPicker";
import { ExportOptionsList } from "./SimpleTableMobileExport/ExportOptionsList";

export type { ExportOptions, SimpleTableMobileExportProps } from "./SimpleTableMobileExport/types";

export function SimpleTableMobileExport<T extends BaseEntity>({
  config,
  data,
  selectedItems,
  filters,
  searchTerm,
  onExport,
  isLoading = false,
}: SimpleTableMobileExportProps<T>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: "csv",
    includeFilters: true,
    includeSelectedOnly: false,
    includeMetadata: true,
    filename: `${config.displayName.toLowerCase().replace(/\s+/g, "_")}_export`,
  });

  const exportStats = useMemo<ExportStats>(() => {
    const totalItems = data.length;
    const selectedCount = selectedItems.length;
    const filteredCount = totalItems;
    const hasActiveFilters = Object.keys(filters).some(
      (key) => filters[key] && filters[key] !== "all"
    );
    const hasSearch = searchTerm.trim().length > 0;

    return {
      totalItems,
      selectedCount,
      filteredCount,
      hasActiveFilters,
      hasSearch,
      willExportCount: exportOptions.includeSelectedOnly ? selectedCount : filteredCount,
    };
  }, [data.length, selectedItems.length, filters, searchTerm, exportOptions.includeSelectedOnly]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      await onExport(exportOptions);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
    }
  }, [exportOptions, onExport]);

  const updateExportOption = useCallback(
    <K extends keyof ExportOptions>(key: K, value: ExportOptions[K]) => {
      setExportOptions((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  if (!config.features?.export) {
    return null;
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsModalOpen(true)}
        disabled={isLoading || data.length === 0}
        className="flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">Export</span>
        {exportStats.willExportCount > 0 && (
          <Badge variant="secondary" className="ml-1">
            {exportStats.willExportCount}
          </Badge>
        )}
      </Button>

      <ResponsiveModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        title="Export Data"
        description={`Export ${config.displayName} data`}
        side="bottom"
        maxWidth="4xl"
      >
        <div className="space-y-4 p-4">
          <ExportSummary stats={exportStats} searchTerm={searchTerm} />

          <ExportFormatPicker
            value={exportOptions.format}
            onChange={(format) => updateExportOption("format", format)}
          />

          <ExportOptionsList
            options={exportOptions}
            selectedItemsCount={selectedItems.length}
            onChange={updateExportOption}
          />

          {/* Filename */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Filename</h3>
            <input
              type="text"
              value={exportOptions.filename}
              onChange={(e) => updateExportOption("filename", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Export filename"
            />
            <p className="text-xs text-gray-500 mt-1">
              File will be saved as: {exportOptions.filename}.{exportOptions.format}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={isExporting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting || exportStats.willExportCount === 0}
              className="flex-1"
            >
              {isExporting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Exporting...
                </div>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Export {exportStats.willExportCount} Records
                </>
              )}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
